import React, { useState } from 'react';
import { Building2, Plus, Save, CheckCircle, AlertCircle } from 'lucide-react';
import { useTenantSetup } from '../../hooks/useTenantSetup';

interface TenantSetupProps {
  onTenantCreated?: (tenantId: string) => void;
}

export const TenantSetup: React.FC<TenantSetupProps> = ({ onTenantCreated }) => {
  const { createTenant: createTenantHook, loading, error } = useTenantSetup();
  
  const [formData, setFormData] = useState({
    hospitalName: 'Lifecare Hospital',
    subdomain: 'lifecare',
    customDomain: 'attendai.lifecare-hospital.com',
    plan: 'professional',
    primaryColor: '#1e40af',
    secondaryColor: '#1e3a8a',
    adminEmail: 'admin@lifecare-hospital.com',
    adminName: 'Dr. Sarah Johnson'
  });
  
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const createTenant = async () => {
    setStatus('idle');
    setMessage('');
    
    try {
      const tenantData: any = {
        hospitalName: formData.hospitalName,
        subdomain: formData.subdomain,
        customDomain: formData.customDomain,
        plan: formData.plan as 'starter' | 'professional' | 'enterprise',
        primaryColor: formData.primaryColor,
        secondaryColor: formData.secondaryColor,
        adminEmail: formData.adminEmail,
        adminName: formData.adminName
      };

      console.log('Calling createTenantHook with data:', tenantData);
      const result = await createTenantHook(tenantData);
      
      if (result.success) {
        setStatus('success');
        setMessage(`✅ ${formData.hospitalName} tenant created successfully! 
        
Setup completed:
• Tenant ID: ${result.tenant?.id || `${formData.subdomain}-hospital`}
• Subdomain: ${formData.subdomain}.attendai.io
• Plan: ${formData.plan}
• Default reminder rules configured

Next steps:
1. Create admin user in Supabase Auth: ${formData.adminEmail}
2. Configure DNS CNAME: ${formData.subdomain}.attendai.io  
3. Test access at https://${formData.subdomain}.attendai.io

IMPORTANT: Create the admin user manually in Supabase Dashboard → Authentication → Users
Email: ${formData.adminEmail}
Password: Set a secure password
The user profile will be created automatically on first login.`);

        if (onTenantCreated && result.tenant) {
          onTenantCreated(result.tenant.id);
        }
      } else {
        setStatus('error');
        setMessage(`❌ Failed to create tenant: ${result.error}`);
      }
    } catch (error: any) {
      console.error('Error in createTenant:', error);
      setStatus('error');
      setMessage(`❌ Failed to create tenant: ${error.message}`);
    }
  };

  const createTenantOld = async () => {
    setStatus('idle');
    setMessage('');
    
    try {
      const tenantDataOld = {
        id: `${formData.subdomain}-hospital`,
        name: formData.hospitalName,
        subdomain: formData.subdomain,
        custom_domain: formData.customDomain || null,
        plan: formData.plan,
        status: 'active',
        branding: {
          primaryColor: formData.primaryColor,
          secondaryColor: formData.secondaryColor,
          hospitalName: formData.hospitalName,
          logo: null
        },
        features: {
          aiPredictions: true,
          automatedReminders: true,
          analytics: true,
          apiAccess: formData.plan !== 'starter',
          customIntegrations: formData.plan === 'enterprise',
          whiteLabeling: formData.plan === 'enterprise'
        },
        limits: {
          maxUsers: formData.plan === 'starter' ? 5 : formData.plan === 'professional' ? 25 : 999,
          maxAppointments: formData.plan === 'starter' ? 1000 : formData.plan === 'professional' ? 10000 : 999999,
          maxPredictions: formData.plan === 'starter' ? 100 : formData.plan === 'professional' ? 1000 : 999999,
          dataRetentionDays: 365
        },
        billing: {
          currentPeriodStart: new Date().toISOString(),
          currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          monthlyPrice: formData.plan === 'starter' ? 99 : formData.plan === 'professional' ? 299 : 999
        }
      };

      // This is the old direct database approach - keeping for reference
      console.log('This method is deprecated');
    } catch (error: any) {
      console.error('Error in createTenantOld:', error);
    }
  };

  // Use error from hook if available
  React.useEffect(() => {
    if (error) {
      setStatus('error');
      if (error.includes('duplicate key value violates unique constraint')) {
        setMessage(`❌ A tenant with subdomain "${formData.subdomain}" already exists. Please choose a different subdomain.`);
      } else {
        setMessage(`❌ Failed to create tenant: ${error}`);
      }
    }
  }, [error, formData.subdomain]);

  return (
    <div className="max-w-4xl mx-auto p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Tenant Setup - Lifecare Hospital</h1>
        <p className="text-gray-600 mt-2">Create and configure a new hospital tenant</p>
      </div>

      <div className="bg-white rounded-lg border p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hospital Name
            </label>
            <input
              type="text"
              value={formData.hospitalName}
              onChange={(e) => setFormData({ ...formData, hospitalName: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subdomain
            </label>
            <div className="flex items-center">
              <input
                type="text"
                value={formData.subdomain}
                onChange={(e) => setFormData({ ...formData, subdomain: e.target.value })}
                className="flex-1 border border-gray-300 rounded-l-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <span className="bg-gray-100 border border-l-0 border-gray-300 rounded-r-lg px-3 py-2 text-gray-600">
                .attendai.io
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom Domain (Optional)
            </label>
            <input
              type="text"
              value={formData.customDomain}
              onChange={(e) => setFormData({ ...formData, customDomain: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Plan
            </label>
            <select
              value={formData.plan}
              onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="starter">Starter ($99/month)</option>
              <option value="professional">Professional ($299/month)</option>
              <option value="enterprise">Enterprise (Custom)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Primary Color
            </label>
            <input
              type="color"
              value={formData.primaryColor}
              onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
              className="w-full h-10 border border-gray-300 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Secondary Color
            </label>
            <input
              type="color"
              value={formData.secondaryColor}
              onChange={(e) => setFormData({ ...formData, secondaryColor: e.target.value })}
              className="w-full h-10 border border-gray-300 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Admin Email
            </label>
            <input
              type="email"
              value={formData.adminEmail}
              onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Admin Name
            </label>
            <input
              type="text"
              value={formData.adminName}
              onChange={(e) => setFormData({ ...formData, adminName: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div className="mt-8 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-medium text-gray-900 mb-2">Preview</h3>
          <div className="space-y-3">
            <div 
              className="p-4 rounded-lg text-white shadow-md"
              style={{ backgroundColor: formData.primaryColor }}
            >
              <h4 className="font-bold text-lg">AttendAI - {formData.hospitalName}</h4>
              <p className="text-sm opacity-90">Healthcare appointment management</p>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center">
                <div 
                  className="w-8 h-8 rounded mx-auto mb-1"
                  style={{ backgroundColor: formData.primaryColor }}
                ></div>
                <p className="text-xs text-gray-600">Primary Color</p>
                <p className="text-xs font-mono text-gray-800">{formData.primaryColor}</p>
              </div>
              <div className="text-center">
                <div 
                  className="w-8 h-8 rounded mx-auto mb-1"
                  style={{ backgroundColor: formData.secondaryColor }}
                ></div>
                <p className="text-xs text-gray-600">Secondary Color</p>
                <p className="text-xs font-mono text-gray-800">{formData.secondaryColor}</p>
              </div>
            </div>
            
            <div className="bg-white border rounded-lg p-3">
              <div className="flex items-center space-x-2 mb-2">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: formData.primaryColor }}
                ></div>
                <span className="text-sm font-medium text-gray-900">Configuration Summary</span>
              </div>
              <div className="space-y-1 text-xs text-gray-600">
                <p><strong>Hospital:</strong> {formData.hospitalName}</p>
                <p><strong>Subdomain:</strong> {formData.subdomain}.attendai.io</p>
                <p><strong>Plan:</strong> {formData.plan.charAt(0).toUpperCase() + formData.plan.slice(1)}</p>
                <p><strong>Admin:</strong> {formData.adminEmail}</p>
                {formData.customDomain && (
                  <p><strong>Custom Domain:</strong> {formData.customDomain}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {status !== 'idle' && (
          <div className={`mt-6 p-4 rounded-lg border flex items-start space-x-3 ${
            status === 'success' 
              ? 'bg-green-50 border-green-200' 
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex-shrink-0">
              {status === 'success' ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-600" />
              )}
            </div>
            <div className="flex-1">
              <pre className={`text-sm whitespace-pre-wrap ${
                status === 'success' ? 'text-green-800' : 'text-red-800'
              }`}>
                {message}
              </pre>
            </div>
          </div>
        )}

        <div className="mt-8 flex justify-end">
          <button
            onClick={createTenant}
            disabled={loading}
            className={`flex items-center space-x-2 px-6 py-2 rounded-lg font-medium transition-colors ${
              loading
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {loading ? (
              <>
                <Building2 className="w-4 h-4 animate-pulse" />
                <span>Creating Tenant...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Create Tenant</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};